/* eslint-disable no-unused-vars */
import { X } from 'lucide-react';
import React from 'react';
import { createOnCallShift } from '../../api';
import { useFeedback } from '../../contexts/FeedbackContext';
import { useSession } from '../../contexts/SessionContext';
import { useWorkspace } from '../../contexts/WorkspaceContext';
import type { UseActionDialogReturn } from '../../hooks/useActionDialog';

type OnCallShiftDialogProps = {
  actionDialogReturn: UseActionDialogReturn;
  onNavigate: (path: string, opts?: { replace?: boolean }) => void;
};

function OnCallShiftDialog({ actionDialogReturn, onNavigate: _onNavigate }: OnCallShiftDialogProps) {
  const {
    actionDialog,
    dialogBusy,
    dialogError,
    setActionDialog,
    setDialogBusy,
    setDialogError,
  } = actionDialogReturn;

  const { session } = useSession();
  const { toast } = useFeedback();
  const { setWorkspaceVersion } = useWorkspace();

  if (!actionDialog || actionDialog.kind !== 'oncall-shift') return null;

  const close = () => { if (!dialogBusy) setActionDialog(null); };

  const canSubmit =
    actionDialog.workIdentityId.trim().length > 0 &&
    actionDialog.startsAt.trim().length > 0 &&
    actionDialog.endsAt.trim().length > 0;

  async function submit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!session || !actionDialog || actionDialog.kind !== 'oncall-shift' || dialogBusy) return;
    setDialogBusy(true);
    setDialogError('');
    try {
      await createOnCallShift(session.accessToken, {
        onCallScheduleId: actionDialog.onCallScheduleId,
        workIdentityId: actionDialog.workIdentityId.trim(),
        startsAt: new Date(actionDialog.startsAt).toISOString(),
        endsAt: new Date(actionDialog.endsAt).toISOString()
      });
      setWorkspaceVersion((v) => v + 1);
      toast('success', `Shift added to "${actionDialog.scheduleName}"`);
      setActionDialog(null);
    } catch (err: any) {
      setDialogError(err?.response?.data?.message || err?.message || 'Something went wrong. Please try again.');
    } finally {
      setDialogBusy(false);
    }
  }

  return (
    <div className="dialogOverlay" role="presentation" onClick={close}>
      <form className="dialogPanel" onClick={(e) => e.stopPropagation()} onSubmit={submit}>
        <div className="dialogHeader">
          <div>
            <div className="panelKicker">On-call</div>
            <h3>Add shift</h3>
            <p>Assign a concrete on-call window to &quot;{actionDialog.scheduleName}&quot;.</p>
          </div>
          <button type="button" className="iconButton" onClick={close} aria-label="Close dialog">
            <X size={16} />
          </button>
        </div>
        <div className="settingsForm">
          <label className="settingsField">
            <span>Work identity ID</span>
            <input
              value={actionDialog.workIdentityId}
              onChange={(e) => setActionDialog({ ...actionDialog, workIdentityId: e.target.value })}
              placeholder="UUID of the on-call operator"
              autoFocus
            />
            <p className="fieldHint">The work identity that owns this shift.</p>
          </label>
          <div className="settingsSplit">
            <label className="settingsField">
              <span>Starts at</span>
              <input
                type="datetime-local"
                value={actionDialog.startsAt}
                onChange={(e) => setActionDialog({ ...actionDialog, startsAt: e.target.value })}
              />
            </label>
            <label className="settingsField">
              <span>Ends at</span>
              <input
                type="datetime-local"
                value={actionDialog.endsAt}
                onChange={(e) => setActionDialog({ ...actionDialog, endsAt: e.target.value })}
              />
            </label>
          </div>
        </div>
        {dialogError && <p className="fieldError">{dialogError}</p>}
        <div className="dialogActions">
          <button type="button" className="secondaryButton" onClick={close} disabled={dialogBusy}>Cancel</button>
          <button type="submit" className="primaryButton" disabled={dialogBusy || !canSubmit}>
            {dialogBusy ? 'Adding…' : 'Add shift'}
          </button>
        </div>
      </form>
    </div>
  );
}

export { OnCallShiftDialog };
